import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import PageLayout from '@/components/PageLayout';
import { supabase } from '@/lib/supabase';
import { useI18n } from '@/i18n/LanguageContext';
import { useCar } from '@/contexts/CarContext';
import { useSettings } from '@/contexts/SettingsContext';
import { Button } from '@/components/ui/button';
import { Edit, Trash2, Eye, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from '@/components/ui/use-toast';

interface OdometerEntry {
  id: string;
  reading: number;
  recorded_at: string;
  source: 'fuel_log' | 'service' | 'manual' | 'fluid_log';
  related_id: string | null;
  entered_by: string | null;
}

const OdometerListPage: React.FC = () => {
  const { carId } = useParams<{ carId: string }>();
  const navigate = useNavigate();
  const { t, formatNumber } = useI18n();
  const { cars } = useCar();
  const { settings } = useSettings();
  
  const [entries, setEntries] = useState<OdometerEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState<OdometerEntry | null>(null);

  const car = cars.find(c => c.id === carId);

  // Group entries by month
  const itemsByMonth = useMemo(() => {
    const grouped: Record<string, OdometerEntry[]> = {};
    
    entries.forEach(entry => {
      const date = new Date(entry.recorded_at);
      const monthKey = format(date, 'yyyy-MM');
      
      if (!grouped[monthKey]) {
        grouped[monthKey] = [];
      }
      grouped[monthKey].push(entry);
    });
    
    // Sort entries within each month by date descending
    Object.keys(grouped).forEach(month => {
      grouped[month].sort((a, b) => 
        new Date(b.recorded_at).getTime() - new Date(a.recorded_at).getTime()
      );
    });
    
    return grouped;
  }, [entries]);

  // Calculate monthly subtotals (sum of readings)
  const monthlySubtotals = useMemo(() => {
    const subtotals: Record<string, number> = {};
    
    Object.entries(itemsByMonth).forEach(([month, monthEntries]) => {
      // For subtotal, we can show the highest reading in that month
      const readings = monthEntries.map(e => e.reading);
      subtotals[month] = Math.max(...readings);
    });
    
    return subtotals;
  }, [itemsByMonth]);

  useEffect(() => {
    if (!carId) return;

    const loadEntries = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('odometer_readings')
          .select('id, reading, recorded_at, source, related_id, entered_by')
          .eq('car_id', carId)
          .order('recorded_at', { ascending: false });

        if (error) throw error;

        setEntries((data || []) as OdometerEntry[]);
      } catch (error) {
        console.error('Error loading odometer entries:', error);
        toast({
          title: t('common.error') || 'Error',
          description: t('common.loadFailed') || 'Failed to load odometer entries',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    loadEntries();
  }, [carId, t]);

  const handleEdit = (entry: OdometerEntry) => {
    // Navigate to add odometer page with entry ID for editing
    navigate(`/odometer/add?carId=${carId}&entryId=${entry.id}`);
  };

  const handleDeleteClick = (entry: OdometerEntry) => {
    setEntryToDelete(entry);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!entryToDelete) return;

    try {
      const { error } = await supabase
        .from('odometer_readings')
        .delete()
        .eq('id', entryToDelete.id);

      if (error) throw error;

      setEntries(prev => prev.filter(e => e.id !== entryToDelete.id));
      setDeleteDialogOpen(false);
      setEntryToDelete(null);
      
      toast({
        title: t('common.success') || 'Success',
        description: t('common.deleted') || 'Entry deleted successfully',
      });
    } catch (error) {
      console.error('Error deleting odometer entry:', error);
      toast({
        title: t('common.error') || 'Error',
        description: t('common.deleteFailed') || 'Failed to delete entry',
        variant: 'destructive',
      });
    }
  };

  const getSourceLabel = (source: string) => {
    const labels: Record<string, string> = {
      'fuel_log': t('odometer.source.fuelLog') || 'Fuel Log',
      'service': t('odometer.source.service') || 'Service',
      'manual': t('odometer.source.manual') || 'Manual',
      'fluid_log': t('odometer.source.fluidLog') || 'Fluid Log',
    };
    return labels[source] || source;
  };

  const formatReading = (reading: number) => {
    if (settings?.distanceUnit === 'mi') {
      // Convert km to miles
      const miles = reading * 0.621371;
      return formatNumber(miles, { maximumFractionDigits: 0 }) + ' mi';
    }
    return formatNumber(reading, { maximumFractionDigits: 0 }) + ' km';
  };

  return (
    <PageLayout
      title={t('odometer.list') || 'Odometer Entries'}
      showBackButton
      onBack={() => navigate(`/cars/${carId}?subTab=info`)}
      className="bg-[#F5F5F5]"
    >
      <div className="min-h-screen font-lato text-black">
        {loading ? (
          <div className="text-center py-8 text-gray-500 p-4">
            {t('common.loading') || 'Loading...'}
          </div>
        ) : (
          <div className="p-4">
            {entries.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 mb-4">{t('odometer.noEntries') || 'No odometer entries found'}</p>
                <Button
                  onClick={() => navigate(`/odometer/add?carId=${carId}`)}
                  className="bg-[#E7FE52] text-black hover:opacity-90"
                >
                  {t('actions.add') || 'Add Entry'}
                </Button>
              </div>
            ) : (
              <>
                {/* Month Groups */}
                {Object.entries(itemsByMonth)
                  .sort(([a], [b]) => {
                    // Sort months descending (newest first)
                    const dateA = new Date(a);
                    const dateB = new Date(b);
                    return dateB.getTime() - dateA.getTime();
                  })
                  .map(([monthKey, monthEntries]) => {
                    const monthDate = new Date(monthKey + '-01');
                    const monthLabel = format(monthDate, 'MMMM yyyy');
                    const subtotal = monthlySubtotals[monthKey];

                    return (
                      <div key={monthKey} className="mb-6">
                        {/* Month Header with Subtotal */}
                        <div className="flex items-center justify-between mb-3 px-2">
                          <h3 className="text-sm font-semibold text-gray-900" style={{ fontFamily: 'Lato' }}>
                            {monthLabel}
                          </h3>
                          <span className="text-sm text-gray-600" style={{ fontFamily: 'Lato' }}>
                            {t('odometer.highest') || 'Highest'}: {formatReading(subtotal)}
                          </span>
                        </div>

                        {/* Entries for this month */}
                        <div className="space-y-2">
                          {monthEntries.map((entry) => (
                            <div
                              key={entry.id}
                              className="bg-white rounded-xl shadow-md p-4 cursor-pointer hover:shadow-lg transition-shadow"
                              onClick={() => handleEdit(entry)}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-sm font-medium text-gray-900" style={{ fontFamily: 'Lato' }}>
                                      {format(new Date(entry.recorded_at), 'dd MMM')}
                                    </span>
                                    <span className="text-sm text-gray-500" style={{ fontFamily: 'Lato' }}>
                                      •
                                    </span>
                                    <span className="text-sm font-semibold text-gray-900" style={{ fontFamily: 'Lato' }}>
                                      {formatReading(entry.reading)}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs text-gray-500" style={{ fontFamily: 'Lato' }}>
                                      {getSourceLabel(entry.source)}
                                    </span>
                                  </div>
                                </div>

                                {/* Action buttons */}
                                <div className="flex items-center gap-2 ml-4" onClick={(e) => e.stopPropagation()}>
                                  <button
                                    onClick={() => handleEdit(entry)}
                                    className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                                    aria-label={t('actions.edit') || 'Edit'}
                                  >
                                    <Edit className="h-4 w-4 text-gray-600" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteClick(entry)}
                                    className="p-2 rounded-full hover:bg-red-50 transition-colors"
                                    aria-label={t('actions.delete') || 'Delete'}
                                  >
                                    <Trash2 className="h-4 w-4 text-red-600" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
              </>
            )}
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('odometer.deleteTitle') || 'Delete Odometer Entry'}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('odometer.deleteConfirm') || 'Are you sure you want to delete this odometer entry? This action cannot be undone.'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('actions.cancel') || 'Cancel'}</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteConfirm}
                className="bg-destructive text-white hover:bg-destructive/90"
              >
                {t('actions.delete') || 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </PageLayout>
  );
};

export default OdometerListPage;

